import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { ListSection } from '../components/ios';

// iOS inset-grouped content row inside a ListSection card.
function Block({ title, children }) {
  return (
    <div className="px-4 py-3">
      {title && <h4 className="font-semibold text-gray-900 mb-2 text-[15px]">{title}</h4>}
      {children}
    </div>
  );
}

function BulletList({ items, bulletClass = 'text-[#8E8E93]' }) {
  return (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start">
          <span className={`${bulletClass} mr-2`}>•</span>
          <span className="text-[15px] text-gray-700">{item}</span>
        </li>
      ))}
    </ul>
  );
}

function PrivacyPolicyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F2F2F7]">
      {/* iOS inline navigation bar: back chevron + centered title */}
      <header className="sticky top-0 z-40 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="relative flex items-center justify-between px-4 h-12 max-w-lg mx-auto">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center -ml-2 text-[#FF3B30] active:opacity-60"
            aria-label="Back"
          >
            <ChevronLeft size={24} />
          </button>
          <h1 className="absolute left-1/2 -translate-x-1/2 text-[16px] font-semibold text-gray-900">
            Privacy Policy
          </h1>
          <span className="w-6" />
        </div>
      </header>

      <div className="max-w-lg mx-auto pb-8">
        <p className="px-8 mt-4 text-[13px] text-[#6D6D72]">Last Updated: November 3, 2024</p>

        <ListSection title="1. Introduction">
          <Block>
            <p className="text-[15px] text-gray-700 leading-relaxed">
              Browne Ink ("we", "our", or "us") operates the CheF mobile application. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our App. Please read this privacy policy carefully.
            </p>
          </Block>
        </ListSection>

        <ListSection title="2. Information We Collect">
          <Block title="2.1 Personal Information">
            <p className="text-[15px] text-gray-700 mb-2">When you register for an account, we collect:</p>
            <BulletList items={[
              'Full name (first and last name)',
              'Email address',
              'Phone number',
              'Profile photo (optional)',
            ]} />
          </Block>
          <Block title="2.2 Delivery Information">
            <p className="text-[15px] text-gray-700 mb-2">For delivery orders, we collect:</p>
            <BulletList items={[
              'Delivery addresses with coordinates',
              'Delivery instructions',
              'Landmarks and apartment numbers',
            ]} />
          </Block>
          <Block title="2.3 Payment Information">
            <p className="text-[15px] text-gray-700">
              Payment card information is collected and processed securely by JADx Cash. We do not store complete card details on our servers.
            </p>
          </Block>
          <Block title="2.4 Location Data">
            <p className="text-[15px] text-gray-700 mb-2">We collect real-time location data from:</p>
            <BulletList items={[
              'Drivers: For order tracking and delivery coordination',
              'Customers: For delivery address verification (optional)',
            ]} />
          </Block>
          <Block title="2.5 Order Information">
            <BulletList items={[
              'Order history and preferences',
              'Menu items ordered',
              'Order timestamps and status',
              'Special instructions',
            ]} />
          </Block>
          <Block title="2.6 Device Information">
            <BulletList items={[
              'Device type and model',
              'Operating system version',
              'Unique device identifiers',
              'Push notification tokens',
            ]} />
          </Block>
          <Block title="2.7 Driver-Specific Information">
            <BulletList items={[
              'Vehicle type and license plate',
              "Driver's license number",
              'Delivery history and earnings',
            ]} />
          </Block>
          <Block title="2.8 Restaurant-Specific Information">
            <BulletList items={[
              'Business name and address',
              'Menu items and pricing',
              'Operating hours',
              'Order history and analytics',
            ]} />
          </Block>
        </ListSection>

        <ListSection title="3. How We Use Your Information">
          <Block>
            <p className="text-[15px] text-gray-700 mb-2">We use collected information for:</p>
            <BulletList items={[
              'Processing and fulfilling orders',
              'Coordinating deliveries between restaurants, drivers, and customers',
              'Processing payments securely',
              'Sending order status notifications',
              'Providing customer support',
              'Improving our services and user experience',
              'Analyzing usage patterns and trends',
              'Preventing fraud and ensuring platform security',
              'Complying with legal obligations',
            ]} />
          </Block>
        </ListSection>

        <ListSection title="Contact Us">
          <Block>
            <p className="text-[15px] text-gray-700">
              If you have questions about this Privacy Policy, please contact us at:
            </p>
            <div className="mt-2 space-y-1">
              <p className="text-[15px] text-gray-700"><strong>Email:</strong> browneinc7@gmail.com</p>
              <p className="text-[15px] text-gray-700"><strong>Phone:</strong> 1 869 660 1052</p>
            </div>
          </Block>
        </ListSection>
      </div>
    </div>
  );
}

export default PrivacyPolicyPage;
